// ErrorBoundary — React error catch for "everything below me" with a
// paper-ink fallback that matches the rest of the system.
//
// React only supports error boundaries as class components — this is the
// one piece of legacy class component we keep. componentDidCatch fires
// after any descendant throws during render / lifecycle / commit. After
// that point, the descendant tree is unmounted and only the fallback
// renders.
//
// Two-level recovery:
//   - Soft retry: bumps a key so the children remount. Good for transient
//     issues (race condition, stale ref). User taps "再試一次".
//   - Hard reload: window.location.reload(). Good for cache poisoning or
//     bundle-vs-SW mismatch. User taps "完全重新整理".
//
// onError callback lets the consumer wire in a reporter (Sentry, custom
// Firestore log). Defaults to console.error so dev gets the stack.

import { Component, type ErrorInfo, type ReactNode } from "react";
import { Han } from "./atoms/Han";
import { TextBtn } from "./atoms/TextBtn";
import { T } from "./tokens";

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional name used in the fallback heading. Helps users (and devs)
   *  know which slice of the UI broke. Default: a generic copy. */
  scope?: string;
  /** Custom fallback. If omitted, the system fallback renders. */
  fallback?: (args: { error: Error; retry: () => void }) => ReactNode;
  /** Called once per caught error. Use this to forward to Sentry etc. */
  onError?: (error: Error, info: ErrorInfo) => void;
  /** Reset boundary when any of these values change. Use when the parent
   *  knows the failure was scoped to old state and a fresh value would
   *  succeed (e.g., route change). */
  resetKeys?: unknown[];
}

interface State {
  error: Error | null;
  retryKey: number;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  override state: State = { error: null, retryKey: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", this.props.scope ?? "(root)", error, info);
    this.props.onError?.(error, info);
  }

  override componentDidUpdate(prev: ErrorBoundaryProps): void {
    if (!this.state.error) return;
    if (!this.props.resetKeys || !prev.resetKeys) return;
    if (this.props.resetKeys.length !== prev.resetKeys.length) return;
    const changed = this.props.resetKeys.some(
      (k, i) => !Object.is(k, prev.resetKeys![i]),
    );
    if (changed) this.retry();
  }

  retry = (): void => {
    this.setState((s) => ({ error: null, retryKey: s.retryKey + 1 }));
  };

  hardReload = (): void => {
    if (typeof window !== "undefined") window.location.reload();
  };

  override render(): ReactNode {
    const { error, retryKey } = this.state;
    const { children, fallback, scope } = this.props;

    if (error) {
      if (fallback) return fallback({ error, retry: this.retry });
      return (
        <div
          style={{
            minHeight: "100dvh",
            display: "grid",
            placeItems: "center",
            background: T.paper,
            padding: 40,
            textAlign: "center",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 18, maxWidth: 320 }}>
            <Han size={11} color={T.ink500} ls="0.3em">
              · 出 了 點 狀 況
            </Han>
            <Han size={15} color={T.ink900} lh={1.7}>
              {scope ? `${scope} 暫時無法顯示` : "畫面暫時無法顯示"}
            </Han>
            <Han size={11} color={T.ink350} lh={1.8}>
              可以先試試重整。若還是不行,請告訴工作員,讓我們協助你。
            </Han>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                marginTop: 14,
                alignSelf: "center",
              }}
            >
              <TextBtn
                onClick={this.retry}
                mono={false}
                size={14}
                ls="0.3em"
                style={{ padding: "10px 0" }}
              >
                · 再 試 一 次
              </TextBtn>
              <TextBtn
                onClick={this.hardReload}
                mono={false}
                size={12}
                color={T.ink350}
                ls="0.3em"
                style={{ padding: "10px 0" }}
              >
                · 完 全 重 新 整 理
              </TextBtn>
            </div>
          </div>
        </div>
      );
    }

    return <div key={retryKey}>{children}</div>;
  }
}
